import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { MessageCircle } from 'lucide-react';
import SEO from '@/components/SEO';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

interface Profile {
  id: string;
  username: string;
  full_name: string;
  avatar_url: string;
  bio: string;
  power: number;
  created_at: string;
  last_login_at: string;
}

const PublicProfile = () => {
  const { username } = useParams<{ username: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProfile();
  }, [username]);

  const fetchProfile = async () => {
    if (!username) return;

    const { data, error } = await supabase
      .from('public_profiles')
      .select('*')
      .eq('username', username)
      .maybeSingle();

    if (error) {
      console.error('Error fetching profile:', error);
      setLoading(false);
      return;
    }

    setProfile(data);
    setLoading(false);
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="min-h-screen flex items-center justify-center">
          <p className="text-muted-foreground">Loading profile...</p>
        </div>
        <Footer />
      </>
    );
  }

  if (!profile) {
    return (
      <>
        <Header />
        <div className="min-h-screen flex flex-col items-center justify-center px-4">
          <h1 className="text-2xl font-rockwell text-primary mb-4">Profile Not Found</h1>
          <p className="text-muted-foreground mb-6">This user does not exist.</p>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SEO
        title={`${profile.full_name || profile.username} - Crown Law Library`}
        description={`View ${profile.full_name || profile.username}'s profile`}
      />
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1 container mx-auto px-4 py-20">

          <Card className="max-w-2xl mx-auto">
            <CardContent className="space-y-6 pt-6">
            {/* Avatar, Full Name and Username */}
            <div className="flex flex-col items-center space-y-4">
              <Avatar className="h-32 w-32">
                <AvatarImage src={profile.avatar_url} />
                <AvatarFallback className="text-2xl">
                  {profile.username?.[0]?.toUpperCase() || 'U'}
                </AvatarFallback>
              </Avatar>
              <div className="text-center space-y-1">
                {profile.full_name && (
                  <h1 className="text-2xl font-rockwell text-primary">
                    {profile.full_name}
                  </h1>
                )}
                <h2 className="text-xl font-semibold text-muted-foreground">
                  @{profile.username}
                </h2>
              </div>
              <div className="text-center">
                  {user && profile && user.id !== profile.id && (
                    <Button
                      onClick={() => {
                        if (!user) {
                          toast.error('Please sign in to send messages');
                          navigate('/auth');
                          return;
                        }
                        navigate(`/messages/${profile.id}`);
                      }}
                      className="mt-4"
                    >
                      <MessageCircle className="mr-2 h-4 w-4" />
                      Send Message
                    </Button>
                  )}
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 py-4 border-y">
                <div className="text-center">
                  <p className="text-2xl font-bold text-primary">{profile.power || 0}</p>
                  <p className="text-sm text-muted-foreground">Power</p>
                </div>
                <div className="text-center">
                  <p className="text-sm font-medium text-muted-foreground">Joined</p>
                  <p className="text-sm">{new Date(profile.created_at).toLocaleDateString()}</p>
                </div>
                <div className="text-center">
                  <p className="text-sm font-medium text-muted-foreground">Last Active</p>
                  <p className="text-sm">
                    {profile.last_login_at 
                      ? new Date(profile.last_login_at).toLocaleDateString()
                      : 'N/A'}
                  </p>
                </div>
              </div>

              {/* Bio */}
              {profile.bio && (
                <div className="space-y-2">
                  <h3 className="font-semibold text-lg">About</h3>
                  <p className="text-muted-foreground whitespace-pre-wrap">
                    {profile.bio}
                  </p>
                </div>
              )}

              {!profile.bio && (
                <p className="text-center text-muted-foreground italic">
                  This user hasn't added a bio yet.
                </p>
              )}
            </CardContent>
          </Card>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default PublicProfile;
